<template>
  <div class="page-content check-out-container">
    <Header></Header>
    <div class="content">
      <PageTitle title="Checkout"></PageTitle>
      <div class="padding-y padding-x fg-2 margin-auto">
        <div class="max-1200">
          <div class="big-body">Shopping Cart</div>
          <div class="gap-3x"></div>

          <div v-if="errors.cart">
            <div class="line-break-gray"></div>
            <div class="gap-3x"></div>
            <div class="error-message cart">{{errors.cart}}</div>
            <div class="gap-3x"></div>
          </div>

          <div v-for="item in checkoutItems" :key="item">
            <div class="line-break-gray"></div>
            <div class="gap-3x"></div>
            <div class="flex-box" v-if="cartItems.indexOf(item) > -1">
              <div class="item-container">
                <div class="item-description">
                  <div class="bold">{{item}}</div>
                </div>
                <div class="number-of-licenses" v-if="$cart.isBuildPlus(item)">
                  <div>Licenses</div>
                  <select class="select" @change="(event) => numberOfAccountsChanged(event.target.value)" v-model="numberOfAccounts" v-if="showNumberOfAccountSelect">
                    <option v-for="option in numberOfAccountsOptions" :value="option.value" :key="option.text">{{option.text}}</option>
                  </select>
                  <input type="text" @change="(event) => numberOfAccountsChanged(event.target.value)" v-model="numberOfAccounts" v-if="!showNumberOfAccountSelect" />
                </div>
              </div>
              <div :class="['item-cost', $cart.isBuildPlus(item) ? 'buildplus' : '']">
                <div class="bold">{{itemCost(item)}} / year</div>
                <div @click="() => removeItem(item)" class="link active">Remove</div>
              </div>
            </div>
            <div class="flex-box" v-if="cartItems.indexOf(item) == -1">
              <div class="flex-grown removed-item">
                  <div>{{item}} was removed from your cart</div>
              </div>
              <div class="calc-right">
                <div class="link active fg-4" @click="() => addItem(item)">Undo</div>
              </div>
            </div>
            <div class="gap-3x"></div>
          </div>

          <div class="line-break-gray"></div>
          <div class="gap-3x"></div>
          <div class="flex-box">
            <div class="item-container bold">
              Total Estimated Cost
            </div>
            <div class="bold item-cost">
              {{annualCost}} / year
            </div>
          </div>
          <div class="gap-3x"></div>
          <div class="line-break-gray"></div>
        </div>
      </div>

      <div class="mobile-gap-6x"></div>
      <div class="padding-x fg-2 margin-auto" id="contact">
        <div class="max-1200">
          <div class="big-body">{{contactSectionText}}</div>
          <div class="gap-3x"></div>
          <div v-if="isBusiness">
            <div class="line-break-gray"></div>
            <div class="gap-3x"></div>
            <div class="bold">Primary account holder. Your licenses will be administered through this account.</div>
            <div class="gap-3x"></div>
          </div>
          <div
            v-for="contact in organizationContacts"
            v-bind:key="organizationContacts.indexOf(contact)"
          >
            <div class="line-break-gray"></div>
            <div class="gap-3x"></div>
            <div class="flex-box data-questions wrap">
              <div class="question-group" v-for="attribute in contactAttributes" :key="attribute.key">
                <div>
                  <SymbiumFormGroup
                    :label="attribute.name"
                    :value="contact[attribute.key]"
                    @update:value="(value) => contactChanged(organizationContacts.indexOf(contact), attribute.key, value)"
                    :error="contact.errors[attribute.key]"
                    inputSize="check-out-input-size"
                  />
                </div>
              </div>
            </div>
            <div class="gap-1x"></div>
            <label class="link active remove-contact" @click="() => removeContact(contact)" v-if="organizationContacts.indexOf(contact) != 0">Remove Contact</label>
            <div v-if="organizationContacts.indexOf(contact) == 0 && isBusiness">
              <div class="gap-3x"></div>
              <div class="line-break-gray"></div>
              <div class="gap-3x"></div>
              <div class="bold">Specify those who should have access through your primary account.  These individuals can be changed at a later time by contacting support@symbium.com.</div>
            </div>
            <div class="gap-3x"></div>
          </div>
          <div class="line-break-gray"></div>
          <div class="gap-3x"></div>
          <label
            class="link active add-contact"
            @click="addContact"
          >+ Add additional contact</label>
          <div class="gap-3x"></div>
          <div class="line-break-gray"></div>
        </div>
      </div>
      <div class="mobile-gap-6x"></div>

      <div class="padding-x fg-2 margin-auto" v-if="isBusiness">
        <div class="max-1200">
          <div class="mobile-gap-6x"></div>
          <div class="desktop-gap-10x"></div>
          <div class="big-body">Licensing Agreement</div>
          <div class="gap-3x"></div>
          <div class="scrollable-text" @scroll="scrollTerm" ref="term-box" id="term-box">
            <div ref="term-text">
              <Terms />
            </div>
          </div>
          <div class="gap-4x"></div>
          <div class="accept-term-button">
            <SymbiumButton
              :text="termAcceptButtonText"
              :onClick="acceptTerm"
              :isSmall="true"
              :isPrimary="isTermAccepted"
              :isDisabled="!isTermRead && !isTermAccepted"
            />
            <div class="error-message" v-if="errors.terms">
              {{errors.terms}}
            </div>
          </div>
          <div class="mobile-gap-6x"></div>
        </div>
      </div>

      <div class="padding-x fg-2 padding-y margin-auto" v-if="isGovernment">
        <div class="max-1200">
          <div class="big-body">Schedule a Meeting</div>
          <div class="gap-3x"></div>
          <div class="line-break-gray"></div>
          <div v-if="errors.startTime">
            <div class="gap-3x" />
            <div class="error-message big">{{errors.startTime}}</div>
            <div class="gap-3x" />
            <div class="line-break-gray" />
          </div>
          <div class="gap-3x"></div>
          <div class="question-text">
            Please book a time slot below for a free consultation.  We will send an additional data questionnaire and video conference invite after you submit your request.
          </div>
          <div class="gap-3x"></div>
          <div class="line-break-gray"></div>
          <div class="calendar computer-view">
            <Calendar
              @time-select="onTimeSelect"
              :inputStartTime="startTime"
              :key="calendarKey"
            ></Calendar>
          </div>
          <div class="calendar mobile-view">
            <MobileCalendar
              @time-select="onTimeSelect"
              :inputStartTime="startTime"
              :key="mobileCalendarKey"
              ></MobileCalendar>
          </div>
          <div class="gap-3x"></div>
          <div class="line-break-gray"></div>
        </div>
      </div>

      <div class="padding-x fg-2 padding-y margin-auto" v-if="isBusiness">
        <div class="max-1200">
          <div class="big-body">Payment</div>
          <div class="gap-3x"></div>
          <div class="line-break-gray"></div>
          <div  v-if="!isFreePurchase">
            <div class="gap-3x"></div>
            <CreditCardForm v-if="paymentType == '0'" :stripe="stripePublicKey" />
            <div class="gap-3x"></div>
            <div class="line-break-gray"></div>
          </div>

          <div class="gap-3x"></div>
          <CouponCode />
          <div class="gap-3x"></div>
          <div class="line-break-gray"></div>
        </div>
      </div>

      <footer class="padding-x gray-form-bg-fill summary-container">
        <div class="max-1200">
          <div class="flex-box vertically-centered margin-auto government" v-if="isGovernment">
            <div class="annual-cost">
              <div class="big-number bold">{{annualCost}}</div>
              <div class="caption-text1 gray">per year</div>
            </div>
            <div class="middle-block">
              <div class="big-number bold">{{selectedTime}}</div>
              <div class="caption-text1 gray">your scheduled meeting</div>
            </div>
            <div class="event-submit">
              <SymbiumButton
                text="Go back"
                :onClick="backToQuestionnaire"
                :isSmall="true"
                :isPrimary="true"
                kclass="hgap-8-right"
              />
              <SymbiumButton
                text="Submit"
                :loading="loading"
                :onClick="submitEvent"
                :isSmall="true"
              />
            </div>
          </div>
          <div class="flex-box vertically-centered margin-auto" v-if="isBusiness">
            <div class="annual-cost">
              <div class="big-number bold">
                {{annualCost}}
                <img
                  :src="`${publicImagePath}/tooltip.png`"
                  class="cost-tooltip"
                  @click="showCostTooltipModal"
                  v-if="discount > 0 && couponCode"
                >
              </div>
              <div class="caption-text1 gray">{{costSummaryLabel}}</div>
            </div>
            <div class="middle-block desktop-only">
              <div class="big-number bold">{{paymentTypeText}}</div>
              <div class="caption-text1 gray">selected payment method</div>
            </div>
            <div class="event-submit" >
              <SymbiumButton
                text="Go back"
                :onClick="backToQuestionnaire"
                :isSmall="true"
                :isPrimary="true"
                kclass="hgap-8-right"
              />
              <SymbiumButton
                text="Purchase"
                :loading="loading"
                :onClick="checkout"
                :isSmall="true"
              />
            </div>
          </div>
        </div>
      </footer>
    </div>
    <CostTooltipModal />
    <MessageModal />
  </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import axios from 'axios';

import SymbiumFormGroup from 'src/components/common/form/SymbiumFormGroup.vue'
import CouponCode from 'src/components/common/CouponCode.vue'
import CreditCardForm from './common/CreditCardForm.vue'
import PageTitle from '../components/layouts/PageTitle.vue'
import Header from '../components/layouts/Header.vue'
import Calendar from "./common/Calendar.vue";
import MobileCalendar from "./common/MobileCalendar.vue";
import Terms from "./common/Terms.vue";
import CostTooltipModal from 'src/components/common/modals/CostTooltipModal.vue'
import EventBus from "./EventBus.js";
import MessageModal from "src/components/common/modals/MessageModal.vue";
import SymbiumButton from 'src/components/common/SymbiumButton.vue'

export default {
  name: 'CheckoutContainer',
  components: {
    Calendar, PageTitle, MobileCalendar,
    CreditCardForm, Terms, Header, CouponCode,
    CostTooltipModal, MessageModal, SymbiumFormGroup, SymbiumButton
  },
  data: function() {
    var paymentOptions = [
      { text: 'Debit/Credit Card', enum: '0' },
      { text: 'ACH', enum: '1' },
    ]
    return {
      startTime: undefined,
      endTime: undefined,
      eventReady: false,
      isTermAccepted: (this.$questionnaire.getAnswer('isTermAccepted').value == 'true'),
      organizationName: this.$questionnaire.getAnswer('organizationName').value,
      isTermRead: false,
      paymentType: this.$questionnaire.getAnswer('paymentType').value || paymentOptions[0].enum,
      paymentOptions,
      stripePublicKey: process.env.VUE_APP_STRIPE_PUBLIC_KEY,
      paymentReady: false,
      organizationContacts: [{errors: {}}],
      checkoutItems: this.$cart.getCheckOutCart(),
      cartItems: this.$cart.loadCart(),
      annualCost: this.$cart.annualCost(this.$questionnaire),
      annualCostAfterDiscount: this.$cart.annualCostInUsd(this.$questionnaire),
      loading: false,
      numberOfAccounts: this.$questionnaire.getAnswer('numberOfAccounts').value,
      showNumberOfAccountSelect: this.$questionnaire.getAnswer('numberOfAccounts').value < 15,
      calendarKey: 0,
      mobileCalendarKey: 0,
      errors: {},
      discount: 0,
      couponCode: null,
    }
  },
  methods: {
    showCostTooltipModal() {
      this.$modal.show('cost-tooltip-modal')
    },
    numberOfAccountsChanged(value) {
      if (value < 5) value = 5;
      this.numberOfAccounts = this.$questionnaire.setAnswer('numberOfAccounts', value).value;
      this.annualCost = this.$cart.annualCost(this.$questionnaire);
      this.showNumberOfAccountSelect = this.numberOfAccounts < 15;
      while (this.organizationContacts.length > value) {
        var contact = this.organizationContacts.pop();
        this.removeContact(contact);
      }
      this.postDiscountCostChanged()
    },
    onTimeSelect: function(time, type) {
      if(time) {
        this.startTime = time;
        this.errors.startTime = null;
        if (type == 'mobile') {
         this.calendarKey = this.calendarKey + 1;
        } else {
          this.mobileCalendarKey = this.mobileCalendarKey + 1;
        }
        this.endTime = moment(this.startTime).add(30, 'minute');
        this.eventReady = this.isEventReady();
        this.$forceUpdate();
      }
    },
    submitEvent() {
      var ready = this.validateContacts();
      ready = this.validateStartTime() && ready;
      ready = this.validateCart() && ready;
      if(!ready) {
        this.showErrors();
        return;
      }
      this.createGoogleCalendarEvent();
    },
    createGoogleCalendarEvent: function() {
      var mainContact = this.organizationContacts[0];
      if(this.loading) return;
      this.loading = true;
      setTimeout(() => {}, 1000)
      this.$createGoogleCalendarEvent(this.startTime, this.endTime, mainContact.firstName, mainContact.email, this.afterCreateGoogleEvent, this.createGoogleEventErrorHandler);
    },
    afterCreateGoogleEvent(googleCalendarEvent) {
      this.$router.push("/shop/thank-you?message=Thanks for your interest!  We’ll contact you shortly.")
      var carts = {};
      _.each(this.cartItems, (item) => {
        carts[item] = this.itemCost(item)
      });
      axios.post(
        process.env.VUE_APP_AUTH_API + 'send_partner_email',
        {
          startTime: this.startTime.format('dddd MMMM D [at] h:mm A'),
          carts,
          annualCost: this.annualCost,
          googleCalendarEvent,
          dataAnswers: {
            "Parcel boundaries": this.$questionnaire.getAnswer("Parcel boundaries").value,
            "Parcel boundaries_link": this.$questionnaire.getAnswer("Parcel boundaries_link").value,
            "Zoning district boundaries": this.$questionnaire.getAnswer("Zoning district boundaries").value,
            "Zoning district boundaries_link": this.$questionnaire.getAnswer("Zoning district boundaries_link").value,
            "Streets": this.$questionnaire.getAnswer("Streets").value,
            "Streets_link": this.$questionnaire.getAnswer("Streets_link").value,
            "Building area & footprints": this.$questionnaire.getAnswer("Building area & footprints").value,
            "Building area & footprints_link": this.$questionnaire.getAnswer("Building area & footprints_link").value,
          },
          contact: this.organizationContacts[0],
        }
      )
    },
    createGoogleEventErrorHandler() {
      this.$modal.show('message-modal', {
        headerMessage: 'Error',
        bodyMessage: "We cannot submit your scheduled event this time. Please try again or contact symbium support at <a href='mailto:support@symbium.com'>support@symbium.com</a>."
      })
      this.loading = false;
    },
    acceptTerm: function() {
      this.isTermAccepted = true;
      this.$questionnaire.setAnswer('isTermAccepted', this.isTermAccepted)
      this.errors.terms = null
    },
    scrollTerm: function(event) {
      var container = event.target;
      if ((container.scrollTop + container.offsetHeight) >= container.scrollHeight) {
        this.isTermRead = true;
      }
    },
    onAnswerChange: function(id, value) {
      this[id] = value;
      this.$questionnaire.setAnswer(id, value);
    },
    freePurchaseDone() {

    },
    checkout() {
      var ready = this.validateCart();
      ready = this.validateTerms() && ready;
      ready = this.validateContacts() && ready;
      if(!ready) {
        this.showErrors();
        return;
      }
      this.$questionnaire.clearContacts();
      _.each(this.organizationContacts, (contact) => {
        _.each(this.contactAttributes, (attribute) => {
          this.$questionnaire.setContactAttribute(
            this.organizationContacts.indexOf(contact),
            attribute.key,
            contact[attribute.key],
          )
        });
      });
      if(this.loading) return;
      this.loading = true;
      if(this.isFreePurchase) {
        this.$requestStripePayment(
          null,
          this.$questionnaire,
          this.couponCode,
          this.afterPaymentSucceded,
          this.afterPaymentFailed,
        );
        return
      }
      if (this.paymentType == '0') {
        EventBus.$emit('checkout-cc', null, this.couponCode)
      } else {
        EventBus.$emit('checkout-bank', this.couponCode)
      }
    },
    removeItem(item) {
      this.$cart.removeItem(item);
      this.cartItems = this.$cart.loadCart();
      this.annualCost = this.$cart.annualCost(this.$questionnaire);
      this.postDiscountCostChanged()
    },
    addItem(item) {
      this.$cart.addItem(item);
      this.cartItems = this.$cart.loadCart();
      this.annualCost = this.$cart.annualCost(this.$questionnaire);
      this.errors.cart = null;
      this.postDiscountCostChanged()
    },
    onPaymentReady: function() {
      this.paymentReady = true;
    },
    onPaymentNotReady: function() {
      this.loading = false;
    },
    backToQuestionnaire: function() {
      this.$router.push('/shop')
    },
    contactChanged(contactIndex, attribute, value) {
      var contact = this.organizationContacts[contactIndex];
      contact[attribute] = value;
      contact.errors[attribute] = this.$questionnaire.validateContact(contact, attribute);
      this.organizationContacts.splice(contactIndex, 1, contact);
    },
    addContact() {
      this.organizationContacts.push({ errors: {}});
      var numberOfAccounts = _.max([this.numberOfAccounts, this.organizationContacts.length]);
      this.numberOfAccountsChanged(numberOfAccounts);
      this.eventReady = this.isEventReady();
    },
    removeContact(contact) {
      this.organizationContacts.splice(this.organizationContacts.indexOf(contact), 1);
    },
    isEventReady() {
      if (this.isGovernment) {
        return this.startTime && (this.cartItems.length > 0);
      } else {
        return this.isTermAccepted && (this.cartItems.length > 0);
      }
    },
    validateContacts() {
      var ready = true;
      _.each(this.organizationContacts, (contact) => {
        var index = this.organizationContacts.indexOf(contact);
        _.each(this.contactAttributes, (attribute) => {
          contact.errors[attribute.key] = this.$questionnaire.validateContact(contact, attribute.key);
          if(contact.errors[attribute.key]) ready = false;
        });
        this.organizationContacts.splice(index, 1, contact);
      });
      if(!ready) {
        document.getElementById('contact').scrollIntoView();
      }
      return ready;
    },
    afterPaymentSucceded() {
      this.$router.push('/shop/thank-you?message=Thanks for your purchase!');
    },
    afterACHSucceded() {
      this.$router.push('/shop/thank-you?message=Thanks for your ACH submission. Please check your email for further instruction!');
    },
    afterBankVerificationFailed() {
      this.$modal.show('message-modal', {
        headerMessage: 'Error',
        bodyMessage: "There was an error when processing your payment.  Please verify your payment information and resubmit payment, or contact us at <a href='mailto:support@symbium.com'>support@symbium.com</a>."
      })
      this.loading = false;
    },
    afterPaymentFailed(error) {
      var bodyMessage
      if(error && error.response && error.response.status == 422) {
        bodyMessage = error.response.data.errors.join('. ') + ". Please verify your account information and resubmit, or contact us at <a href='mailto:support@symbium.com'>support@symbium.com</a>."
      } else {
        bodyMessage = "There was an error when processing your payment.  Please verify your payment information and resubmit payment, or contact us at <a href='mailto:support@symbium.com'>support@symbium.com</a>."
      }
      this.$modal.show('message-modal', { headerMessage: 'Error', bodyMessage })
      this.loading = false;
    },
    itemCost(item) {
      return this.$cart.itemCost(item, this.$questionnaire);
    },
    validateStartTime() {
      var ready = true;
      if(!this.startTime) {
        this.errors.startTime = "Please select a meeting date and time";
        ready = false
      }
      return ready;
    },
    validateTerms() {
      if(!this.isTermAccepted) {
        this.errors.terms = "Please read and accept the terms"
      }
      return this.isTermAccepted
    },
    validateCart() {
      var isEmptyCart = this.cartItems.length == 0;
      if(isEmptyCart) {
        this.errors.cart = "Please select at least one product";
      }

      return !isEmptyCart;
    },
    showErrors() {
      if(_.keys(this.errors).length > 0) {
        this.$nextTick(function () {
          var errorMessages = document.getElementsByClassName('error-message');
          if(errorMessages.length > 0) {
            errorMessages[0].scrollIntoView();
            window.scrollBy(0, -100);
          }
        });
      }
    },
    applyCoupon(data) {
      this.discount = data.discount;
      this.annualCost = this.$cart.annualCost(this.$questionnaire, this.discount)
      this.couponCode = data.coupon_code
    },
    postDiscountCostChanged() {
      EventBus.$emit('cost-changed', this.$cart.annualCostInUsd(this.$questionnaire));
    }
  },
  computed: {
    isFreePurchase() {
      return this.annualCostAfterDiscount < 2
    },
    costSummaryLabel() {
      return this.couponCode ? 'for the first year' : 'per year'
    },
    contactSectionText() {
      return this.isBusiness ? 'User Accounts' : 'Contact Information';
    },
    selectedTime: function() {
      if (!this.startTime) return '--'
      return this.formattedStartTime;
    },
    termAcceptButtonText: function() {
      return this.isTermAccepted ? 'Accepted' : 'I have read and accept the terms';
    },
    isGovernment: function() {
      return this.$questionnaire.isGovernment();
    },
    isBusiness: function() {
      return this.$questionnaire.isBusiness();
    },
    numberOfAccountsOptions() {
      return [
        { value: 5, text: '5', },
        { value: 6, text: '6', },
        { value: 7, text: '7', },
        { value: 8, text: '8', },
        { value: 9, text: '9', },
        { value: 10, text: '10', },
        { value: 11, text: '11', },
        { value: 12, text: '12', },
        { value: 13, text: '13', },
        { value: 14, text: '14', },
        { value: 15, text: '15+', },
      ]
    },
    paymentTypeText: function() {
      return this.paymentOptions[parseInt(this.paymentType)].text;
    },
    contactAttributes: function() {
      return [
        { name: "First Name", key: 'firstName' },
        { name: "Last Name", key: 'lastName' },
        { name: "Email", key: 'email' },
        { name: "Phone", key: 'phone' },
      ];
    },
    formattedStartTime() {
      return this.startTime ? this.startTime.format(' MMMM D, h:mm A') : undefined;
    }
  },
  watch: {
    paymentType() {
      this.paymentReady = false;
    },
    annualCost() {
      this.annualCostAfterDiscount = this.$cart.annualCostInUsd(this.$questionnaire) - this.discount
    }
  },
  mounted() {
    if (!this.checkoutItems) {
      this.$router.push('/shop');
    }
    window.scrollTo(0,0);
    if (this.$refs['term-box'] && this.$refs['term-box'].offsetHeight >= this.$refs['term-box'].scrollHeight) {
      document.getElementById('term-box').style.height = (this.$refs['term-text'].scrollHeight - 20).toString() + 'px';
    }
  },
  created(){
    EventBus.$on('payment-ready', this.onPaymentReady)
    EventBus.$on('payment-not-ready', this.onPaymentNotReady)
    EventBus.$on('payment-in-progress', () => this.loading = true)
    EventBus.$on('payment-suceeded', this.afterPaymentSucceded)
    EventBus.$on('ach-suceeded', this.afterACHSucceded)
    EventBus.$on('bank-verification-failed', this.afterBankVerificationFailed)
    EventBus.$on('payment-failed', this.afterPaymentFailed)
    EventBus.$on('coupon-applied', this.applyCoupon);
  },
  beforeDestroy(){
    EventBus.$off('payment-ready', this.onPaymentReady)
    EventBus.$off('payment-not-ready', this.onPaymentNotReady)
    EventBus.$off('payment-in-progress', () => this.loading = true)
    EventBus.$off('payment-suceeded', this.afterPaymentSucceded)
    EventBus.$off('ach-suceeded', this.afterACHSucceded)
    EventBus.$off('bank-verification-failed', this.afterBankVerificationFailed)
    EventBus.$off('payment-failed', this.afterPaymentFailed)
    EventBus.$off('coupon-applied', this.applyCoupon);
  }
}
</script>

<style lang="scss">
.check-out-container {
  .credit-card-inputs {
    .question-group {
      &:nth-child(1){
        flex: 3;
        min-width: 300px;
      }
      &:nth-child(2), &:nth-child(3), &:nth-child(4){
        flex: 1;
        min-width: 200px;
      }
      &:nth-child(4) {
        margin-right: 32px;
      }
    }
  }
}
</style>
<style lang="scss" scoped>

footer {
  height: 120px;
  width: 100%;

  @media screen and (max-width: 768px) {
    height: auto;
  }
}

.check-out-input-size {
  width: calc(min(320px, 90vw)) !important;
}

.content {
  padding-bottom: 120px;
}

.scrollable-text {
  padding: 32px;
  height: 334px;
  border: 1px solid #DBE0FF;
  overflow: scroll;

  @media screen and (max-width: 768px) {
    padding: 8px;
    font-size: 12px;
    line-height: 16px;
  }
}

.scrollable-text::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE and Edge */
.scrollable-text {
  -ms-overflow-style: none;
}

.accept-term-button .oval-button {
  float: none;
}

.event-submit {
  flex-grow: 1;
  display: flex;
  justify-content: flex-end;
}

.item-container {
  flex-grow: 1;
  display: flex;

  @media screen and (max-width: 600px) {
    display: block;
  }
}

.item-cost.buildplus {
  @media screen and (max-width: 600px) {
    padding-top: 24px;
  }
}

.item-cost {
  .link {
    text-align: right;
  }
}

.item-description {
  flex-grow: 1;

  @media screen and (max-width: 600px) {
    .link {
      display: none;
    }

  }
}

.number-of-licenses {
  margin-right: 116px;

  input {
    width: 64px;
    padding: 0;
    height: 24px;
  }

  select {
    width: 40px;
    border: none !important;
    height: 24px;
    padding: 0;
  }


  @media screen and (max-width: 864px) {
    input, select {
      width: 36px !important;
    }
  }
}

.add-contact, .remove-contact {
  margin-bottom: 0;
  font-weight: 600;
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 0.07px;
}

.go-back {
  display: inline-block;
  height: 48px;
  line-height: 48px;
  width: calc(100% - 148px);
  text-align: right;
  cursor: pointer;

  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.07px;
  color: #5160EC;

  @media screen and (max-width: 864px) {
    display: none;
  }
}

.summary-container .vertically-centered {
  .annual-cost {
    width: calc(25% - 20px);
    min-width: 96px;

    @media screen and (max-width: 768px) {
      margin-right: 0;
    }
  }

  @media screen and (max-width: 768px) {
    align-items: unset
  }
}

.removed-item, .removed-item * {
  padding-right: 8px;
  color: #CBCFF9;
}

// .oval-button.event-submit-button {
//   width: 112px;
// }
// .oval-button.purchase-submit-button {
//   width: 128px;
// }

.cart.error-message {
  font-size: 16px;
  line-height: 24px;
  margin-top: 0;
}
</style>
